import styled from 'styled-components';
import { ChangeEvent, ReactNode, useEffect, useState, MutableRefObject } from 'react';
import { debounce } from 'lodash';
import { hideImageUpload } from 'src/constants/restrictedCommunities';
import { useSelector } from 'src/store/store';
import EmojiPickerContainer from 'src/components/textEditor/components/emojiPickerContainer';
import MarkButton, { ButtonType } from 'src/components/textEditor/components/buttons';
import { Format } from 'src/components/textEditorSimple/components/contentEditor';

type ToolbarProps = {
  dataTestId: string,
  className?: string,
  editorRef: MutableRefObject<HTMLDivElement>,
  flushChanges: () => void;
  format: Format,
  hasImage?: boolean,
  hasMentions?: boolean;
  isEmojiPickerClosed?: boolean,
  isFocused: boolean,
  onAddImg?: () => void,
  setFormat: (format: Format) => void,
  setUploadImage: (image: unknown) => void;
  submitButton?: ReactNode,
  uploadImgId?: string, // always use unique strings if having multiple img inputs on 1 page else img gets inserted to 1st editor
}

export default function Toolbar({
  dataTestId,
  className,
  editorRef,
  flushChanges,
  format,
  hasImage = true,
  hasMentions = true,
  isEmojiPickerClosed, // to use for closing from outside
  isFocused,
  onAddImg,
  setFormat,
  setUploadImage,
  submitButton = null,
  uploadImgId,
}: ToolbarProps): JSX.Element {
  const [isShowEmojiPicker, setIsShowEmojiPicker] = useState(false);
  const isImageUploadVisible = useShowImageUpload(hasImage);

  const focusNow = () => editorRef.current.focus();

  const focusEndOfReply = () => {
    focusNow();
    if (typeof window.getSelection != 'undefined' && typeof document.createRange != 'undefined') { // Modern browsers
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'createTextRange' does not exist on type ... Remove this comment to see the full error message
    } else if (typeof document.body.createTextRange != 'undefined') { // Older browsers
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'createTextRange' does not exist on type ... Remove this comment to see the full error message
      const textRange = document.body.createTextRange();
      textRange.moveToElementText(editorRef.current);
      textRange.collapse(false);
      textRange.select();
    }
  };

  const addMentionEntry = () => {
    if (!isFocused) focusEndOfReply();
    const selection = window.getSelection();
    const range = selection?.getRangeAt(0);
    range?.deleteContents();
    const newNode = document.createTextNode('@');
    range?.insertNode(newNode);
    range?.setStartAfter(newNode);
    range?.setEndAfter(newNode);
    setTimeout(() => flushChanges(), 300); // this way, the focus is not lost and regained - was causing an issue in FF predictive text, resetting node
  };

  const addEmoji = (emoji: { native: string }) => {
    if (!isFocused) focusEndOfReply();
    const selection = window.getSelection();
    const range = selection?.getRangeAt(0);
    range?.deleteContents();
    const newNode = document.createTextNode(emoji.native);
    range?.insertNode(newNode);
    range?.setStartAfter(newNode);
    range?.setEndAfter(newNode);
    setTimeout(() => flushChanges(), 300);
  };

  const addImage = (e: ChangeEvent<HTMLInputElement>) => {
    if (isImageUploadVisible) {
      const currentFiles = e.target.files; // FileList object (similar to NodeList)
      if (!currentFiles || currentFiles && currentFiles.length === 0) return;
      const imageUrl = URL.createObjectURL(currentFiles[0]);
      setUploadImage({ url: imageUrl, file: currentFiles[0], description: null, hash: null });
      if (onAddImg) onAddImg();
    }
  };

  const focusSelection = () => {
    focusNow();
    const sel = window.getSelection();
    const range = sel?.getRangeAt(0);
    sel?.addRange(range);
  };

  const toggleFormat = (type: ButtonType) => {
    focusSelection();
    if (type) {
      document.execCommand(type, false, null);
      setFormat({ ...format, [type]: !format[type] });
    }
  };

  const debouncedToggleFormat = debounce(toggleFormat, 200);

  useEffect((() => {
    if (isEmojiPickerClosed) setIsShowEmojiPicker(false);
  }), [isEmojiPickerClosed]);

  return (
    <>
      <MenuContainer
        aria-label="Text editor toolbar"
        className={className}
        data-testid={dataTestId}
        tabIndex={0} // to focus tab on Tab key
      >
        <MarkButton
          aria="Add bold"
          format="bold"
          isActive={!!format['bold']}
          toggleMark={debouncedToggleFormat}
        />
        <MarkButton
          aria="Add italic"
          format="italic"
          isActive={!!format['italic']}
          toggleMark={debouncedToggleFormat}
        />
        <MarkButton
          aria="Add underline"
          format="underline"
          isActive={!!format['underline']}
          toggleMark={debouncedToggleFormat}
        />
        {hasMentions && (
          <MarkButton
            aria="Add mention"
            format="at"
            isActive={false}
            toggleMark={addMentionEntry}
          />
        )}
        <MarkButton
          aria="Add emoji"
          format="smile"
          isActive={false}
          toggleMark={() => setIsShowEmojiPicker(!isShowEmojiPicker)}
        />
        {isImageUploadVisible && (
          <>
            <MarkButton
              aria="Add image"
              format="picture"
              htmlFor={uploadImgId || 'editor-img-upload'}
              isActive={false}
              toggleMark={() => { }}
            />
            <UploadInput
              accept="image/*"
              id={uploadImgId || 'editor-img-upload'}
              tabIndex={-1} // not reachable via tabs
              type="file"
              onChange={addImage}
              onClick={(event) => {
                // needs to clear out the target else same file selection won't register in onchange
                (event.target as HTMLInputElement).value = '';
              }}
            />
          </>
        )}
        {submitButton}
      </MenuContainer>
      {isShowEmojiPicker && <EmojiPickerContainer onSelect={addEmoji} />}
    </>
  );
}

const useShowImageUpload = (hasImage: boolean) => {
  const slug = useSelector(({ context }) => context.currentCommunity?.slug);
  if (!slug) return false;
  if (hideImageUpload.includes(slug)) return false;
  return hasImage;
};

const MenuContainer = styled.div<{ width?: string }>`
    vertical-align: bottom;
    width: 100%;
    border-top: 1px solid ${({ theme }) => theme.colorGreyLight};
    padding: 10px 0;
    margin-bottom: 5px; // margin - in safari, there's another 'padding' for outline, and bottom of outline would be hidden
    margin-top: 10px;
    & > * {
      display: inline-block;
    }
  `;

const UploadInput = styled.input`
  position: absolute;
  left: -999em;
  opacity: 0;
`;
